import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanSearch, Cpu, Calculator, ShieldCheck, Scale, ArrowRight, MapPin, Layers, GitMerge, ListChecks } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AddressSearch from "../components/AddressSearch";
import { usePageSeo } from "../lib/seo";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";

const CANON = `${ENTITY_SITE}/how-it-works`;

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

const PILLARS = [
  {
    icon: ScanSearch, title: "3-Portal AVM Benchmarking",
    body: "We cross-check a live Zillow Zestimate® (via Bridge Interactive) against Redfin and Homes.com figures, plus a RentCast automated valuation drawn from recent comparable sales. Several independent models — not a single black-box estimate — set your baseline range.",
  },
  {
    icon: Cpu, title: "Multi-AI Comparative Engine",
    body: "Three independent AI appraisers — Google Gemini, OpenAI, and Anthropic Claude — each weigh the same local comparables for proximity, recency, size, and lot differences, applying a 45% marginal size adjustment and showing the math behind every number.",
  },
  {
    icon: Calculator, title: "Real Net Equity Calculation",
    body: "We deduct your jurisdiction's transfer taxes, escrow and title fees, and model a 1% Equitas listing fee against a traditional 3% — so you see the equity you actually keep, not just a headline price.",
  },
];

const STEPS = [
  { icon: MapPin, title: "Enter your address", body: "No sign-up, no obligation. We identify your property in Santa Clara County and pull its characteristics." },
  { icon: Layers, title: "We gather the evidence", body: "A live RentCast AVM, up to 10 recent comparable sales, and a live Zillow Zestimate® (via Bridge) are assembled into one evidence packet." },
  { icon: Cpu, title: "Three AI appraisers weigh in", body: "Gemini, OpenAI, and Claude each reason over the SAME comparable sales — no web searching, no outside guesses — and return an independent, defensible valuation with adjustments shown." },
  { icon: GitMerge, title: "We reconcile a range", body: "Our engine takes the median of the three models, bounds it to the comparable evidence, and scores how closely they agree (High, Moderate, or Divergent)." },
  { icon: ListChecks, title: "You see your true net equity", body: "An itemized seller net sheet applies transfer taxes and a 1% listing fee versus a traditional 3%, so you know what you'd actually keep. Every figure is editable." },
];

const FAQ = [
  ["How does the Equitas Multi-AI & 3-Portal home valuation tool work?",
    "It benchmarks three real-estate portal estimates — a live Zillow Zestimate® (via Bridge Interactive), Redfin, and Homes.com — alongside a three-model AI appraisal panel (Google Gemini, OpenAI, and Anthropic Claude) that reasons over local comparable sales. An algorithm then reconciles them into a single defensible range and applies local transfer taxes and a 1% listing fee to show your real net equity."],
  ["Do the AI models search the web or use Zillow data to value my home?",
    "No. The AI appraisers are evidence-grounded: each one reasons strictly over the same licensed comparable-sales packet we provide — they do not browse the web, scrape listing portals, or pull in outside numbers. The live Zillow Zestimate® is shown to you for comparison but is firewalled out of the AI panel, so it never contaminates the independent appraisal."],
  ["Why compare three portal AVMs and three AI models instead of one estimate?",
    "Any single automated estimate can be off in a Silicon Valley micro-market, where a school boundary or lot difference can swing value materially. Comparing Zillow, Redfin, and Homes.com alongside a three-model AI panel exposes the spread between sources, so you can judge confidence and avoid mispricing your home."],
  ["Is the automated estimate an official appraisal?",
    "No. These are automated valuation estimates for informational purposes only — not an appraisal or a guaranteed sale price. For a precise opinion of value, Louis Fong provides a full comparative market analysis. All commissions and fees are negotiable and are not set by law."],
  ["How do you calculate my net proceeds at a 1% listing fee?",
    "After estimating value, the tool applies your jurisdiction's transfer taxes (for example, the Santa Clara County $1.10-per-$1,000 documentary transfer tax, plus San Jose Measure E where it applies), standard escrow and title fees, and any buyer-side compensation you enter. It then compares a 1% Equitas listing fee against a traditional 3% to show the equity delta you keep."],
];

export default function HowItWorks() {
  usePageSeo({
    title: "How Our Home Valuation Works | Multi-AI & 3-Portal AVM | Equitas",
    description: "See how Equitas Homes' Santa Clara County home valuation works — 3 portal AVMs (Zillow, Redfin, Homes.com) plus a 3-model AI appraisal panel, reconciled into your real net equity at a 1% listing fee.",
    canonical: CANON,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        brokerageNode(),
        agentNode(),
        {
          "@type": "WebApplication", "@id": `${CANON}#tool`,
          name: "Equitas Multi-AI & 3-Portal AVM Home Valuation Tool",
          url: `${ENTITY_SITE}/`,
          applicationCategory: "RealEstateApplication",
          operatingSystem: "Web",
          description: "A free Santa Clara County home-valuation tool that benchmarks a live Zillow Zestimate® (via Bridge Interactive), Redfin, and Homes.com against a three-model AI appraisal panel (Google Gemini, OpenAI, Anthropic Claude) and reconciles them into a seller's real net equity at a 1% listing fee.",
          featureList: ["3-Portal AVM benchmarking", "Multi-AI appraisal panel", "Deterministic median reconciliation", "Seller net equity calculator", "1% vs 3% listing fee comparison"],
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@id": `${ENTITY_SITE}/#brokerage` },
        },
        {
          "@type": "HowTo", "@id": `${CANON}#howto`,
          name: "How the Equitas home valuation works",
          step: STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
        },
        {
          "@type": "FAQPage", "@id": `${CANON}#faq`,
          mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
        },
        breadcrumbNode(`${CANON}#breadcrumb`, [
          { name: "Home", item: `${ENTITY_SITE}/` },
          { name: "How It Works", item: CANON },
        ]),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col" data-testid="how-it-works-page">
      <Header />
      <main className="flex-1">
        {/* HERO */}
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 lg:py-28">
            <motion.div {...fade}>
              <span className="eyebrow">Multi-AI · 3-Portal AVM</span>
              <h1 className="mt-5 font-serif font-light leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
                How your home valuation<br /><span className="text-brand-gold">actually works.</span>
              </h1>
              {/* AEO direct-answer opening */}
              <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-brand-cream/80">
                The Equitas home value tool benchmarks three real-estate portals — a live Zillow Zestimate® (via Bridge Interactive), Redfin, and Homes.com — against a three-model AI appraisal panel, then reconciles them into one defensible range and your real net equity at a 1% listing fee. No single black-box number; no guesswork.
              </p>
            </motion.div>
          </div>
        </section>

        {/* THREE PILLARS */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="how-pillars">
          <motion.div {...fade} className="max-w-2xl">
            <span className="eyebrow">The Method</span>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">Three layers of evidence</h2>
            <p className="mt-4 text-brand-stone leading-relaxed">We combine independent market models, independent AI reasoning, and transparent net-equity math — so your estimate is both accurate and explainable.</p>
          </motion.div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <motion.div key={p.title} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="how-pillar-card">
                <div className="h-11 w-11 rounded-xl bg-brand-emerald/10 flex items-center justify-center"><p.icon className="h-5 w-5 text-brand-emerald" /></div>
                <h3 className="mt-4 font-serif text-xl text-brand-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-stone leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 5-STEP SEQUENCE */}
        <section className="bg-brand-sand/60 border-y border-brand-hairline">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 lg:py-24" data-testid="how-steps">
            <motion.div {...fade}>
              <span className="eyebrow">Step by Step</span>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-brand-ink">From address to net equity in five steps</h2>
            </motion.div>
            <ol className="mt-12 space-y-8">
              {STEPS.map((s, i) => (
                <motion.li key={s.title} {...fade} transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="flex gap-5" data-testid="how-step">
                  <div className="shrink-0 flex flex-col items-center">
                    <div className="h-12 w-12 rounded-full bg-brand-emerald text-brand-cream flex items-center justify-center font-serif text-xl">{i + 1}</div>
                    {i < STEPS.length - 1 && <div className="mt-2 w-px flex-1 bg-brand-hairline" />}
                  </div>
                  <div className="pb-2">
                    <h3 className="flex items-center gap-2 font-serif text-xl text-brand-ink"><s.icon className="h-5 w-5 text-brand-gold" /> {s.title}</h3>
                    <p className="mt-2 text-sm sm:text-base text-brand-stone leading-relaxed max-w-2xl">{s.body}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        {/* EVIDENCE FIREWALL / TRUST */}
        <section className="mx-auto max-w-5xl px-5 sm:px-8 py-16 lg:py-20" data-testid="how-trust">
          <div className="grid gap-6 sm:grid-cols-2">
            <motion.div {...fade} className="rounded-2xl bg-white border border-brand-hairline p-7">
              <ShieldCheck className="h-6 w-6 text-brand-emerald" />
              <h3 className="mt-4 font-serif text-xl text-brand-ink">Evidence-grounded, not AI guesswork</h3>
              <p className="mt-2 text-sm text-brand-stone leading-relaxed">Each AI appraiser reasons only over the licensed comparable sales we supply — no web browsing, no portal scraping. The live Zillow Zestimate® is shown for your reference but firewalled out of the panel, so the AI value stays independent.</p>
            </motion.div>
            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.08 }} className="rounded-2xl bg-white border border-brand-hairline p-7">
              <Scale className="h-6 w-6 text-brand-emerald" />
              <h3 className="mt-4 font-serif text-xl text-brand-ink">A defensible, reconciled range</h3>
              <p className="mt-2 text-sm text-brand-stone leading-relaxed">No single model decides your value. A deterministic algorithm takes the median of the three AI appraisers, bounds it to the comparable evidence, and tells you how closely they agree — so you can judge confidence honestly.</p>
            </motion.div>
          </div>
          <p className="mt-8 text-xs text-brand-stone/70 leading-relaxed max-w-3xl">
            Home value estimates are automated and for informational purposes only — not an appraisal or a guaranteed sale price. All brokerage commissions and fees are negotiable and are not set by law.
          </p>
        </section>

        {/* FAQ */}
        <section className="bg-brand-sand/60 border-y border-brand-hairline">
          <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20" data-testid="how-faq">
            <motion.h2 {...fade} className="font-serif text-3xl sm:text-4xl text-brand-ink">Frequently asked questions</motion.h2>
            <div className="mt-8 divide-y divide-brand-hairline">
              {FAQ.map(([q, a], i) => (
                <motion.div key={i} {...fade} transition={{ duration: 0.45, delay: i * 0.04 }} className="py-5" data-testid="how-faq-item">
                  <h3 className="font-serif text-lg text-brand-ink">{q}</h3>
                  <p className="mt-2 text-sm text-brand-stone leading-relaxed">{a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA with live address search */}
        <section className="bg-brand-dark text-brand-cream">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 py-20 lg:py-24 text-center" data-testid="how-cta">
            <motion.div {...fade}>
              <span className="eyebrow text-brand-gold">Try it now</span>
              <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
                See your home's value and real net equity.
              </h2>
              <p className="mt-5 max-w-2xl mx-auto text-brand-cream/70 leading-relaxed">
                Enter your address to run the full 3-Portal &amp; Multi-AI valuation — free, instant, and with no obligation.
              </p>
              <div className="mt-9 text-left max-w-2xl mx-auto">
                <AddressSearch />
              </div>
              <p className="mt-6 text-sm text-brand-cream/60">
                Prefer a human read? <Link to="/contact" className="text-brand-gold hover:underline" data-testid="how-contact-link">Schedule a free equity consultation with Louis Fong</Link>.
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
